import React, { useEffect } from "react";
import { MultiselectItem, MultiselectOnChangeResponse, QESMultiSelect } from "@app/shared/qes-standards/forms/Multiselect";
import { CheckboxStateEnum, QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { Client, ClientType } from "@app/client/client";

interface TemplateUpdateProps {
  selectedClients: string[]
  setSelectedClients: React.Dispatch<React.SetStateAction<string[]>>;
}

export const ClientSelection: React.FC<React.PropsWithChildren<TemplateUpdateProps>> = (props: TemplateUpdateProps) => {
  const [allClientsSelected, setAllClientsSelected] = React.useState<boolean>(false);
  const [clients, setClients] = React.useState<Client[]>([]);

  useEffect(() => {
    //clientService.getAccessReportClients().then((x) => setClients(x));
    // temporary hard-coded clients
    const clients: Client[] = [{
      authorizedInsurances: [],
      bulkUploadLimitHourWindow: 12,
      bulkUploadRecordLimit: 50000,
      clientId: 1,
      clientType: ClientType.Issuer,
      crossProjectDatabaseConnectionId: null,
      dashboards: [],
      databaseConnectionId: null,
      dateDeactivated: "",
      defaultUserRoles: [],
      disableAccessCacheReads: false,

      disableAccessCacheWrites: false,
      domains: [],
      enterpriseComplianceDashboardDatabaseConnectionId: null,
      isActive: true,
      isDataShareEligible: false,
      isQuestInternal: true,
      isTwoSidedRegulatorEligible: false,
      logoUrl: "",
      name: "Cool Client 1",
      networkBenchmarkingDataRights: null,
      networkBenchmarkingDataSource: false,
      networkServicingProviderReportLimit: 1000000,
      outreachClientXid: "",
      outreachMetricsActivationDate: null,
      pciDashboardDatabaseConnectionId: null,
      prospectMarketServicingProviderReportLimit: 400000,
      providerAffiliationAuthorizedStates: null,
      questViewerGuestUserRoles: [],
      quinnWeeklyPromptLimit: 0,
      resourceAdminGuestUserRoles: [],
      roles: [],
      sfAccountId: "",
      skipTerminationTimeDistanceRecalcDefaultValue: false,
      snowflakeBackgroundWarehouseDefault: null,
      snowflakeBackgroundWarehouseMax: null,
      snowflakeBackgroundWarehouseMin: null,
      snowflakeWarehouseDefault: null,
      snowflakeWarehouseMax: null,
      snowflakeWarehouseMin: null,
      softwareReleases: [],
      storageConnectionString: "",
      storageContainerName: ""
    }, {
      authorizedInsurances: [],
      bulkUploadLimitHourWindow: 12,
      bulkUploadRecordLimit: 50000,
      clientId: 2,
      clientType: ClientType.Issuer,
      crossProjectDatabaseConnectionId: null,
      dashboards: [],
      databaseConnectionId: null,
      dateDeactivated: "",
      defaultUserRoles: [],
      disableAccessCacheReads: false,
      disableAccessCacheWrites: false,
      domains: [],
      enterpriseComplianceDashboardDatabaseConnectionId: null,
      isActive: true,
      isDataShareEligible: false,
      isQuestInternal: true,
      isTwoSidedRegulatorEligible: false,
      logoUrl: "",
      name: "Evil Client 2",
      networkBenchmarkingDataRights: null,
      networkBenchmarkingDataSource: false,
      networkServicingProviderReportLimit: 1000000,
      outreachClientXid: "",
      outreachMetricsActivationDate: null,
      pciDashboardDatabaseConnectionId: null,
      prospectMarketServicingProviderReportLimit: 400000,
      providerAffiliationAuthorizedStates: null,
      questViewerGuestUserRoles: [],
      quinnWeeklyPromptLimit: 0,
      resourceAdminGuestUserRoles: [],
      roles: [],
      sfAccountId: "",
      skipTerminationTimeDistanceRecalcDefaultValue: false,
      snowflakeBackgroundWarehouseDefault: null,
      snowflakeBackgroundWarehouseMax: null,
      snowflakeBackgroundWarehouseMin: null,
      snowflakeWarehouseDefault: null,
      snowflakeWarehouseMax: null,
      snowflakeWarehouseMin: null,
      softwareReleases: [],
      storageConnectionString: "",
      storageContainerName: ""
    }, {
      authorizedInsurances: [],
      bulkUploadLimitHourWindow: 12,
      bulkUploadRecordLimit: 50000,
      clientId: 3,
      clientType: ClientType.Issuer,
      crossProjectDatabaseConnectionId: null,
      dashboards: [],
      databaseConnectionId: null,
      dateDeactivated: "",
      defaultUserRoles: [],
      disableAccessCacheReads: false,
      disableAccessCacheWrites: false,
      domains: [],
      enterpriseComplianceDashboardDatabaseConnectionId: null,
      isActive: true,
      isDataShareEligible: false,
      isQuestInternal: true,
      isTwoSidedRegulatorEligible: false,
      logoUrl: "",
      name: "Boring Client 3",
      networkBenchmarkingDataRights: null,
      networkBenchmarkingDataSource: false,
      networkServicingProviderReportLimit: 1000000,
      outreachClientXid: "",
      outreachMetricsActivationDate: null,
      pciDashboardDatabaseConnectionId: null,
      prospectMarketServicingProviderReportLimit: 400000,
      providerAffiliationAuthorizedStates: null,
      questViewerGuestUserRoles: [],
      quinnWeeklyPromptLimit: 0,
      resourceAdminGuestUserRoles: [],
      roles: [],
      sfAccountId: "",
      skipTerminationTimeDistanceRecalcDefaultValue: false,
      snowflakeBackgroundWarehouseDefault: null,
      snowflakeBackgroundWarehouseMax: null,
      snowflakeBackgroundWarehouseMin: null,
      snowflakeWarehouseDefault: null,
      snowflakeWarehouseMax: null,
      snowflakeWarehouseMin: null,
      softwareReleases: [],
      storageConnectionString: "",
      storageContainerName: ""
    }];

    setClients(clients);
  }, []);

  const clientOptions: { [key: string]: MultiselectItem } = React.useMemo(() => {
    const clientObject = {};

    clients.forEach((x) => Object.assign(clientObject,
      {
        [x.clientId.toString()]: {
          currentState: props.selectedClients.includes(x.clientId.toString()) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
          displayValue: x.name,
        }
      }));

    return clientObject;
  }, [clients, props.selectedClients]);

  const handleSettingAllClientsSelected = (checked: boolean): void => {
    if (checked) {
      props.setSelectedClients(clients.map(x => x.clientId.toString()));
    } else {
      props.setSelectedClients([]);
    }

    setAllClientsSelected(checked);
  }

  const updateSelectedClients = (response: MultiselectOnChangeResponse): void => {
    props.setSelectedClients(response.checkedKeys)
  }

  return (
    <React.Fragment>
      <div className="row">
        <div className="col-12">
          <label className="label-s mb-0">Select at least one client</label>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <QESCheckbox
            checked={allClientsSelected}
            label="Apply Changes to All Clients"
            testId="access-reports-admin__template-update--all-clients"
            onChange={(e) => handleSettingAllClientsSelected(e.target.checked)}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <QESMultiSelect
            containerClassName="mb-3"
            label=""
            onChange={updateSelectedClients}
            options={clientOptions}
            testId="access-reports-admin__template-update--clients-multiselect"
          />
        </div>
      </div>
    </React.Fragment>
  );
}